import { queryOptions } from '@tanstack/react-query'
import { ApiError, apiExchange, apiGet } from '../../api/client'
import { saveBytes } from '../../api/inventoree'
import type { AppState } from '../../api/businessApps'

/**
 * Harta aplicatiilor - `v4/api/src/Controller/BusinessAppsMapController.php`.
 *
 *   GET /business-apps/map?company=&ips=1             aplicatiile, serverele, gazdele, constatarile
 *   GET /business-apps/map/export.pdf?company=&ips=1  acelasi document pe A4
 *
 * `company` conteaza numai cu "All companies" in bara.
 */

export type ComponentState = 'down' | 'alarm' | 'offline' | 'stale' | 'degraded' | 'unmonitored' | 'online' | 'unknown'

export type MapComponent = {
  key: string
  deviceId: number | null
  ciId: number | null
  name: string
  type: string
  office: string
  state: ComponentState
  stateLabel: string
  silentDays: number | null
  hasAgent: boolean
  virtual: boolean
  hostId: number | null
  hostName: string
  inCmdb: boolean
  openAlarms: number
  criticalAlarms: number
  openTickets: number
  /** Celelalte aplicatii care stau pe aceeasi masina. */
  sharedWith: string[]
  /** Numai cu `ips=1`. */
  ip?: string
}

export type MapApp = {
  id: number
  name: string
  vendor: string
  version: string
  companyName: string
  criticality: number
  criticalityLabel: string
  supportVendor: string
  contractNo: string
  responseHours: number | null
  schedule: string
  contactCount: number
  state: AppState
  stateLabel: string
  openAlarms: number
  openTickets: number
  groups: { role: number; roleLabel: string; components: MapComponent[] }[]
}

export type MapHost = {
  id: number
  name: string
  office: string
  state: ComponentState
  stateLabel: string
  guests: { name: string; apps: string[] }[]
  apps: string[]
  criticalApps: number
  ip?: string
}

/** Legatura intre doua aplicatii: `from` depinde de `to` ("Opera PMS depends on Charisma ERP"). */
export type MapLink = {
  id: number
  fromId: number
  toId: number
  from: string
  to: string
  description: string
  inCmdb: boolean
}

export type Severity = 'high' | 'medium' | 'low'

export type MapFinding = {
  rule: string
  severity: Severity
  title: string
  detail: string
  recommendation: string
  apps: string[]
  criticality: number
}

export type MapSummary = {
  apps: number
  critical: number
  servers: number
  physicalHosts: number
  workstations: number
  findings: number
  high: number
  medium: number
  low: number
  sentence: string
}

export type ApplicationMapPage = {
  ready: boolean
  needsCompany: boolean
  message?: string
  includeIps: boolean
  generatedAt: string
  pdfAvailable: boolean
  companyId: number
  companyName?: string
  companies?: { id: number; name: string }[]
  summary?: MapSummary
  apps?: MapApp[]
  hosts?: MapHost[]
  /** Legaturile dintre aplicatii; lipseste pe un server fara dependente. */
  links?: MapLink[]
  findings?: MapFinding[]
}

function params(company: number, ips: boolean): string {
  const q = new URLSearchParams()
  if (company > 0) q.set('company', String(company))
  if (ips) q.set('ips', '1')
  const s = q.toString()
  return s === '' ? '' : `?${s}`
}

export function applicationMapQuery(company: number, ips: boolean) {
  return queryOptions({
    queryKey: ['business-apps', 'map', company, ips],
    queryFn: () => apiGet<ApplicationMapPage>(`/business-apps/map${params(company, ips)}`),
  })
}

/**
 * "Download PDF": documentul facut de server din aceleasi date. Cand serverul
 * n-are cu ce sa-l scoata raspunde cu o propozitie, si aia se arata - nu se
 * salveaza un fisier care nu e un PDF.
 */
export async function downloadApplicationMapPdf(company: number, ips: boolean): Promise<void> {
  const out = await apiExchange(`/business-apps/map/export.pdf${params(company, ips)}`, { method: 'GET' })
  if (!out.type.includes('pdf')) {
    let message = 'The PDF could not be produced.'
    try {
      message = (JSON.parse(out.text) as { message?: string }).message ?? message
    } catch {
      /* Raspunsul nu era JSON; ramane propozitia de mai sus. */
    }
    throw new ApiError(out.status || 501, message)
  }
  saveBytes(out.bytes, out.type, out.filename, 'application-map.pdf')
}
